import { useCallback, useEffect, useRef, useState } from "react"
import {
  fetchCbeAccessToken,
  isCbeAuthError,
  isCbePaymentSuccess,
  payWithCbeSuperApp,
} from "@/lib/cbe-superapp"
import { isCbeSuperAppAvailable } from "@/lib/payment-provider"
import { getCbeWindow, type CbePaymentResult } from "@/types/cbe-superapp"

type CbePaymentStatus = "idle" | "fetching_token" | "ready" | "paying" | "success" | "error"

export type UseCbeSuperAppPaymentOptions = {
  /**
   * Optionally prefetch a SuperApp user token via SDK.
   * Backend already generates xAccessToken — leave false unless you need to override.
   */
  autoFetchToken?: boolean
  /** If true, try SDK fetchAccessToken before pay and pass it as override */
  preferSdkAccessToken?: boolean
  appName?: string
}

export type PayWithCbeArgs = {
  transactionId: string
  amount: number
  title: string
}

/**
 * React hook for CBE SuperApp Mini App payments.
 *
 * Flow (backend-owned token):
 * 1. User taps Pay → createOrder (backend returns orderPayload + authPayload + xAccessToken)
 * 2. window.cbesuperapp.initiatePayment(...) → native payment popup
 * 3. On ft_number → success; caller refreshes order from own API
 */
export function useCbeSuperAppPayment(options: UseCbeSuperAppPaymentOptions = {}) {
  const {
    autoFetchToken = false,
    preferSdkAccessToken = false,
    appName = "Qetero",
  } = options

  const [available, setAvailable] = useState(false)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [status, setStatus] = useState<CbePaymentStatus>("idle")
  const [error, setError] = useState<string | null>(null)
  const [lastResult, setLastResult] = useState<CbePaymentResult | null>(null)

  const mountedRef = useRef(true)
  const accessTokenRef = useRef<string | null>(null)

  useEffect(() => {
    mountedRef.current = true
    setAvailable(isCbeSuperAppAvailable())
    return () => {
      mountedRef.current = false
      const w = getCbeWindow()
      delete w.handleAccessToken
      delete w.handlePaymentResult
    }
  }, [])

  const clearAccessToken = useCallback(() => {
    accessTokenRef.current = null
    if (mountedRef.current) setAccessToken(null)
  }, [])

  const refreshAvailability = useCallback(() => {
    const isAvailable = isCbeSuperAppAvailable()
    setAvailable(isAvailable)
    return isAvailable
  }, [])

  const ensureAccessToken = useCallback(async (forceRefresh = false): Promise<string> => {
    if (!forceRefresh && accessTokenRef.current) return accessTokenRef.current

    if (!isCbeSuperAppAvailable()) {
      throw new Error("CBE_SUPERAPP_UNAVAILABLE")
    }

    if (mountedRef.current) {
      setStatus("fetching_token")
      setError(null)
    }

    try {
      if (forceRefresh) clearAccessToken()
      const token = await fetchCbeAccessToken()
      if (!mountedRef.current) return token
      accessTokenRef.current = token
      setAccessToken(token)
      setStatus("ready")
      return token
    } catch (err: any) {
      clearAccessToken()
      if (mountedRef.current) {
        setStatus("error")
        setError(err?.message || "Failed to fetch CBE access token")
      }
      throw err
    }
  }, [clearAccessToken])

  useEffect(() => {
    const isAvailable = refreshAvailability()
    if (!autoFetchToken || !isAvailable) return
    ensureAccessToken(false).catch(() => {})
  }, [autoFetchToken, ensureAccessToken, refreshAvailability])

  const pay = useCallback(
    async (args: PayWithCbeArgs): Promise<CbePaymentResult> => {
      if (!isCbeSuperAppAvailable()) {
        const err = new Error("CBE_SUPERAPP_UNAVAILABLE")
        setStatus("error")
        setError(err.message)
        throw err
      }

      setStatus("paying")
      setError(null)
      setLastResult(null)

      try {
        // Optional SDK override; otherwise backend xAccessToken is used as-is
        let sdkToken: string | undefined
        if (preferSdkAccessToken) {
          try {
            sdkToken = await ensureAccessToken(true)
          } catch {
            // Fall through — backend already embeds xAccessToken
            sdkToken = undefined
          }
        }

        const runPay = () =>
          payWithCbeSuperApp({
            transactionId: args.transactionId,
            amount: args.amount,
            title: args.title,
            accessToken: sdkToken,
            appName,
          })

        let result: CbePaymentResult
        try {
          result = await runPay()
        } catch (firstErr: any) {
          if (!preferSdkAccessToken || !isCbeAuthError(firstErr)) throw firstErr
          clearAccessToken()
          sdkToken = await ensureAccessToken(true)
          result = await payWithCbeSuperApp({
            transactionId: args.transactionId,
            amount: args.amount,
            title: args.title,
            accessToken: sdkToken,
            appName,
          })
        }

        if (!mountedRef.current) return result

        setLastResult(result)

        if (isCbePaymentSuccess(result)) {
          setStatus("success")
        } else {
          setStatus("error")
          setError("CBE_PAYMENT_FAILED")
        }

        return result
      } catch (err: any) {
        if (mountedRef.current) {
          setStatus("error")
          setError(err?.message || "CBE payment failed")
        }
        throw err
      }
    },
    [appName, clearAccessToken, ensureAccessToken, preferSdkAccessToken]
  )

  return {
    available,
    accessToken,
    status,
    error,
    lastResult,
    ensureAccessToken,
    clearAccessToken,
    pay,
    isCbePaymentSuccess,
    refreshAvailability,
  }
}
